import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Banner from "../Components/Banner";
import { eventPageData } from "../Constants/data.jsx";
import { Helmet } from "react-helmet-async";
import { useEvents } from "../hooks/useEvents";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate } from "react-router-dom";

const Event = () => {
  const [currentPageUpcoming, setCurrentPageUpcoming] = useState(0);
  const [currentPagePast, setCurrentPagePast] = useState(0);
  const [eventsPerPage, setEventsPerPage] = useState(6);

  const navigate = useNavigate();
  const { getDetailedEvents, loading } = useEvents();
  const apiEvents = getDetailedEvents();

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 992;
      setEventsPerPage(mobile ? 3 : 6);
      setCurrentPageUpcoming(0);
      setCurrentPagePast(0);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const now = new Date();
  now.setHours(23, 59, 59, 999);

  const parseLocalDate = (dateValue) => {
    if (!dateValue) return null;

    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return null;

    date.setHours(23, 59, 59, 999);
    return date;
  };

  const getEventEndDate = (event) => {
    if (event.endDate) {
      const endDate = parseLocalDate(event.endDate);
      if (endDate) return endDate;
    }

    if (event.startDate) {
      const startDate = parseLocalDate(event.startDate);
      if (startDate) return startDate;
    }

    return null;
  };

  const getEventStartDate = (event) => {
    if (event.startDate) {
      const startDate = parseLocalDate(event.startDate);
      if (startDate) return startDate;
    }
    return null;
  };

  let upcomingEvents = apiEvents.filter((event) => {
    const eventEndDate = getEventEndDate(event);

    if (!eventEndDate) return true;

    return eventEndDate >= now;
  });

  let pastEvents = apiEvents.filter((event) => {
    const eventEndDate = getEventEndDate(event);

    if (!eventEndDate) return false;

    return eventEndDate < now;
  });

  // Sort upcoming events in ascending order (earliest first)
  upcomingEvents = [...upcomingEvents].sort((a, b) => {
    const startDateA = getEventStartDate(a);
    const startDateB = getEventStartDate(b);
    
    if (!startDateA && !startDateB) return 0;
    if (!startDateA) return 1;
    if (!startDateB) return -1;
    
    return startDateA - startDateB;
  });

  // Sort past events in descending order (most recent first)
  pastEvents = [...pastEvents].sort((a, b) => {
    const startDateA = getEventStartDate(a);
    const startDateB = getEventStartDate(b);
    
    if (!startDateA && !startDateB) return 0;
    if (!startDateA) return 1;
    if (!startDateB) return -1;
    
    return startDateB - startDateA;
  });

  useEffect(() => {
    if (!loading && apiEvents.length > 0) {
      console.log("=== Event Filtering Debug ===");
      console.log("Current date:", now);
      console.log("Total events:", apiEvents.length);
      console.log("Upcoming events:", upcomingEvents.length);
      console.log("Past events:", pastEvents.length);

      apiEvents.forEach((event) => {
        const endDate = getEventEndDate(event);
        const category = endDate && endDate < now ? "PAST" : "UPCOMING";
        console.log(
          `${category}: ${event.title} - End Date: ${
            endDate ? endDate.toDateString() : "No date"
          }`
        );
      });
    }
  }, [loading, apiEvents]);

  useEffect(() => {
    setCurrentPageUpcoming(0);
    setCurrentPagePast(0);
  }, [apiEvents.length, eventsPerPage]);

  const totalPagesUpcoming = Math.ceil(upcomingEvents.length / eventsPerPage);
  const totalPagesPast = Math.ceil(pastEvents.length / eventsPerPage);

  const startIndexUpcoming = currentPageUpcoming * eventsPerPage;
  const endIndexUpcoming = startIndexUpcoming + eventsPerPage;
  const currentUpcomingEvents = upcomingEvents.slice(
    startIndexUpcoming,
    endIndexUpcoming
  );

  const startIndexPast = currentPagePast * eventsPerPage;
  const endIndexPast = startIndexPast + eventsPerPage;
  const currentPastEvents = pastEvents.slice(startIndexPast, endIndexPast);

  const nextPageUpcoming = () => {
    if (currentPageUpcoming < totalPagesUpcoming - 1) {
      setCurrentPageUpcoming((prev) => prev + 1);
    }
  };

  const prevPageUpcoming = () => {
    if (currentPageUpcoming > 0) {
      setCurrentPageUpcoming((prev) => prev - 1);
    }
  };

  const nextPagePast = () => {
    if (currentPagePast < totalPagesPast - 1) {
      setCurrentPagePast((prev) => prev + 1);
    }
  };

  const prevPagePast = () => {
    if (currentPagePast > 0) {
      setCurrentPagePast((prev) => prev - 1);
    }
  };

  const handleEventClick = (eventSlug) => {
    navigate(`/event/${eventSlug}`);
  };

  const skeletonItems = Array.from({ length: eventsPerPage }, (_, index) => (
    <div className="col-12" key={`skeleton-${index}`}>
      <div className="py-5 bg-white">
        <div className="row align-items-center">
          <div className="col-12 col-lg-4 mb-4 mb-lg-0">
            <Skeleton
              height={250}
              width="100%"
              style={{
                borderRadius: "10px",
                width: "100%",
              }}
            />
          </div>

          <div className="col-12 col-lg-8">
            <div className="mb-4">
              <Skeleton
                height={10}
                width="75%"
                className="mb-3"
                borderRadius="20px"
              />
              <Skeleton
                height={10}
                width="55%"
                className="mb-3"
                borderRadius="20px"
              />
              <Skeleton
                height={10}
                width="65%"
                className="mb-3"
                borderRadius="20px"
              />
              <Skeleton
                height={10}
                width="70%"
                className="mb-4"
                borderRadius="20px"
              />
            </div>

            <Skeleton
              height={12}
              width="95%"
              className="mb-2"
              borderRadius="20px"
            />
            <Skeleton
              height={12}
              width="85%"
              className="mb-2"
              borderRadius="20px"
            />
            <Skeleton
              height={12}
              width="70%"
              className="mb-4"
              borderRadius="20px"
            />

            <Skeleton
              height={42}
              width={150}
              style={{ borderRadius: "999px" }}
            />
          </div>
        </div>
      </div>
    </div>
  ));

  const renderEventsSection = (
    title,
    events,
    currentEvents,
    currentPage,
    totalPages,
    nextPage,
    prevPage,
    showPagination
  ) => {
    if (!loading && events.length === 0) return null;

    return (
      <div className="row">
        <div className="col-12">
          <h4 className="fw-bold mt-5 text-md-center fs-1 heading">{title}</h4>
        </div>

        {loading
          ? skeletonItems
          : currentEvents.map((ev, idx) => (
              <motion.div
                key={`${ev.id}-${idx}-${currentPage}-${eventsPerPage}`}
                className="col-12"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              >
                <div className="py-5 bg-white">
                  <div className="row align-items-center">
                    <motion.div
                      className="col-12 col-lg-4 mb-4 mb-lg-0"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    >
                      <img
                        src={ev.image}
                        alt={ev.title}
                        className="img-fluid rounded shadow-lg"
                        style={{
                          height: "250px",
                          objectFit: "cover",
                          width: "100%",
                        }}
                        onError={(e) => {
                          e.target.src =
                            "https://via.placeholder.com/400x250/f8f9fa/6c757d?text=Event+Image";
                        }}
                      />
                    </motion.div>

                    <motion.div
                      className="col-12 col-lg-8"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    >
                      <div className="mb-4 text-md-start">
                        <div className="mb-3">
                          <span className="fw-bold me-2">Event:</span>
                          <span className="fw-bold">{ev.title}</span>
                        </div>

                        <div className="mb-3">
                          <span className="fw-bold me-2">Venue:</span>
                          <span>{ev.venue}</span>
                        </div>

                        <div className="mb-2">
                          <span className="fw-bold me-2">Start Date:</span>
                          <span className="me-3">{ev.formattedStartDate}</span>
                          <span className="fw-bold me-2">Start Time:</span>
                          <span>{ev.formattedStartTime}</span>
                        </div>

                        <div>
                          <span className="fw-bold me-2">End Date:</span>
                          <span className="me-3">
                            {ev.endDate ? ev.formattedEndDate : "Not mentioned"}
                          </span>

                          <span className="fw-bold me-2">End Time:</span>
                          <span>
                            {ev.endTime ? ev.formattedEndTime : "Not mentioned"}
                          </span>
                        </div>
                      </div>

                      <p className="mb-4 fs-5 text-muted lh-sm">
                        {ev.subtitle}
                      </p>

                      <div className="mt-4">
                        <button
                          onClick={() => handleEventClick(ev.slug)}
                          className="btn btn-warning hover-button text-white btn-sm px-4 py-2"
                        >
                          More Details{" "}
                          <i className="bi bi-arrow-right ms-2"></i>
                        </button>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            ))}

        {!loading && showPagination && events.length > eventsPerPage && (
          <div className="col-12">
            <div className="d-flex justify-content-center align-items-center mt-1 mb-4 gap-3">
              <button
                className="bi bi-arrow-left cursor-pointer fs-4 heading custom-border d-flex align-items-center justify-content-center rounded-circle"
                onClick={prevPage}
                disabled={currentPage === 0}
              />
              <span className="mx-3 fs-5 heading">
                Page {currentPage + 1} of {totalPages}
              </span>
              <button
                className="bi bi-arrow-right cursor-pointer fs-4 heading custom-border d-flex align-items-center justify-content-center rounded-circle"
                onClick={nextPage}
                disabled={currentPage === totalPages - 1}
              />
            </div>
          </div>
        )}

        {!loading && currentEvents.length === 0 && events.length > 0 && (
          <div className="text-center py-5">
            <p className="text-muted">No events in this section.</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>Event - Sydney Sri Ayyappa Swami Centre</title>
        <meta
          name="description"
          content="Sydney Sri Ayyappa Swami Centre is a spiritual hub for Lord Ayyappa devotees, offering poojas, bhajans, festivals, and community services in Sydney."
        />
        <meta
          name="keywords"
          content="Sydney Sri Sri Ayyappa Swami Centre, Ayyappa Temple Sydney, Lord Ayyappa Temple Australia, Hindu Temple in Sydney, Ayyappa Pooja Sydney, Swami Ayyappa Devotees Sydney, Sabarimala Ayyappa Sydney, Hindu Religious Centre Sydney, Ayyappa Bhajans Sydney, Indian Hindu Temple Sydney"
        />
        <meta property="og:title" content="Sydney Sri Ayyappa Swami Centre" />
        <meta
          property="og:description"
          content="Sydney Sri Ayyappa Swami Centre is a spiritual hub for Lord Ayyappa devotees, offering poojas, bhajans, festivals, and community services in Sydney."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sydneysriayyappa.com/" />
        <meta
          property="og:see_also"
          content="https://www.youtube.com/@SydneySriAyyappaSwamiCentre"
        />
        <meta
          property="og:see_also"
          content="https://www.instagram.com/sydneysriayyappa/"
        />
        <meta
          property="og:see_also"
          content="https://www.facebook.com/sydneyayyappa"
        />
        <meta
          property="fb:admins"
          content="https://www.facebook.com/sydneyayyappa"
        />
        <meta
          name="instagram:title"
          content="Sydney Sri Ayyappa Swami Centre"
        />
        <meta
          name="instagram:description"
          content="Sydney Sri Ayyappa Swami Centre is a spiritual hub for Lord Ayyappa devotees, offering poojas, bhajans, festivals, and community services in Sydney."
        />
        <meta name="instagram:site" content="@sydneysriayyappa" />
      </Helmet>

      <div className="container-fluid px-0">
        <Banner bannerData={eventPageData} />

        <section className="py-4 py-lg-5">
          <div className="container px-3 px-lg-0">
            {renderEventsSection(
              <span className="fs-2 d-block text-center text-md-start">
                Upcoming Events
              </span>,
              upcomingEvents,
              currentUpcomingEvents,
              currentPageUpcoming,
              totalPagesUpcoming,
              nextPageUpcoming,
              prevPageUpcoming,
              upcomingEvents.length > eventsPerPage
            )}

            {renderEventsSection(
              <span className="fs-2 d-block text-center text-md-start">
                Past Events
              </span>,
              pastEvents,
              currentPastEvents,
              currentPagePast,
              totalPagesPast,
              nextPagePast,
              prevPagePast,
              pastEvents.length > eventsPerPage
            )}

            {!loading && apiEvents.length === 0 && (
              <div className="text-center py-5">
                <h4 className="fw-bold my-5 text-md-center fs-1 heading">
                  {eventPageData.mainHeading}
                </h4>
                <p className="text-muted">
                  We are preparing our upcoming spiritual events for you. Please
                  stay tuned.
                </p>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default Event;